import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowRight,
  faCalendarAlt,
  faCheckCircle,
  faExclamationTriangle,
  faGift,
  faPause,
  faPhone,
  faRobot,
} from '@fortawesome/free-solid-svg-icons';
import useUser from '../../../hooks/useUser';
import { pushEvent, ANALYTICS_EVENTS } from '../../../utils/analytics';
import ContentModal from '../../../components/UnifiedModal/variants/ContentModal';
import type { SubscriptionRecord } from '../../../types/account';
import { getErrorMessage } from '../../../utils/errorMessage';
import DeploymentImpactWarning from './DeploymentImpactWarning';
import './SubscriptionCancellationModal.css';

const DEFAULT_API_URL = 'https://localhost:8082';
const MIN_EXPLANATION_LENGTH = 20;
const MIN_INTENT_EXPLANATION_LENGTH = 15;

const steps = [
  'initial',
  'deployment_impact',
  'reason',
  'reason_details',
  'original_intent',
  'intent_details',
  'pause_preference',
  'call_interest',
  'complete',
  'error',
] as const;

type CancellationStep = typeof steps[number];
type RequestHeaders = {
  'Content-Type': string;
  Authorization?: string;
};

type CancellableSubscription = SubscriptionRecord & {
  price?: number | null;
  billingCycle?: string | null;
  stripeSubscriptionId?: string | null;
};

interface SubscriptionCancellationModalProps {
  show: boolean;
  onHide?: () => void;
  subscription: CancellableSubscription;
  onComplete?: (result?: unknown) => void;
}

const surveySteps: CancellationStep[] = [
  'reason',
  'reason_details',
  'original_intent',
  'intent_details',
  'pause_preference',
  'call_interest',
];

const shuffleArray = <T,>(items: T[]): T[] => {
  const shuffled = [...items];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const nextIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[nextIndex]] = [shuffled[nextIndex], shuffled[index]];
  }
  return shuffled;
};

const formatPrice = (subscription?: CancellableSubscription | null): string | null => {
  const rawAmount = subscription?.amount ?? subscription?.price;

  if (typeof rawAmount !== 'number' || Number.isNaN(rawAmount)) {
    return null;
  }

  const normalizedAmount = rawAmount > 999 ? rawAmount / 100 : rawAmount;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(normalizedAmount);
};

const SubscriptionCancellationModal = ({
  show,
  onHide,
  subscription,
  onComplete,
}: SubscriptionCancellationModalProps) => {
  const { getApiToken, isAuthenticated } = useUser();
  const apiBaseUrl = process.env.REACT_APP_API_URL || DEFAULT_API_URL;

  const [currentStep, setCurrentStep] = useState<CancellationStep>('initial');
  const [cancellationReason, setCancellationReason] = useState('');
  const [cancellationExplanation, setCancellationExplanation] = useState('');
  const [originalIntent, setOriginalIntent] = useState('');
  const [originalIntentExplanation, setOriginalIntentExplanation] = useState('');
  const [pausePreference, setPausePreference] = useState('');
  const [interestedInCall, setInterestedInCall] = useState(false);
  const [acceptedOfferType, setAcceptedOfferType] = useState<string | null>(null);
  const [deploymentStepShown, setDeploymentStepShown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const planName = subscription?.planName || 'your subscription';
  const billingInterval = subscription?.interval || subscription?.billingCycle || 'month';
  const formattedPrice = useMemo(() => formatPrice(subscription), [subscription]);
  const isDemoSubscription = String(subscription?.id || '').startsWith('demo_') || planName.toLowerCase().includes('demo');

  const randomizedReasons = useMemo(() => shuffleArray([
    'Too expensive for my budget',
    'Not using it enough to justify cost',
    'Found a better alternative',
    'Technical issues or bugs',
    'Strategies not performing well',
    'Missing features I need',
    'Changed my trading approach',
    'Temporary financial constraints',
    'Too complicated to use',
    'Not meeting my expectations',
    'Other reason',
  ]), []);

  const randomizedIntents = useMemo(() => shuffleArray([
    'Automated profitable trading',
    'Creating my own AI trading agent',
    'Creating my own trading algorithm',
    'Better backtesting capabilities',
    'Advanced market analytics',
    'Portfolio diversification tools',
    'Risk management features',
    'Real-time trading alerts',
    'Educational trading content',
    'Paper trading practice',
    'Other goal',
  ]), []);

  useEffect(() => {
    if (!show) return;

    window.localStorage.removeItem('feedbackCallBooked');
    setCurrentStep('initial');
    setCancellationReason('');
    setCancellationExplanation('');
    setOriginalIntent('');
    setOriginalIntentExplanation('');
    setPausePreference('');
    setInterestedInCall(false);
    setAcceptedOfferType(null);
    setDeploymentStepShown(false);
    setLoading(false);
    setError('');

    pushEvent({
      event: ANALYTICS_EVENTS.CANCELLATION_MODAL_OPENED,
      subscription_id: subscription?.id,
      plan_name: subscription?.planName,
    });
  }, [show, subscription?.id, subscription?.planName]);

  const scrollModalTop = (): void => {
    window.setTimeout(() => {
      document.querySelector('.cancellation-modal .content-modal-body')?.scrollTo({ top: 0 });
    }, 50);
  };

  const buildHeaders = async (): Promise<RequestHeaders> => {
    const token = await getApiToken?.();
    const headers: RequestHeaders = { 'Content-Type': 'application/json' };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  };

  const surveyData = () => ({
    cancellationReason: cancellationReason || null,
    cancellationExplanation: cancellationExplanation || null,
    originalIntent: originalIntent || null,
    originalIntentExplanation: originalIntentExplanation || null,
    pausePreference: pausePreference || null,
    interestedInCall,
  });

  const savePartialResponse = async (): Promise<void> => {
    const partialData = {
      userProductId: subscription?.id,
      surveyData: surveyData(),
      isPartial: true,
      lastCompletedStep: currentStep,
      timestamp: new Date().toISOString(),
    };

    if (isDemoSubscription || isAuthenticated === false) {
      pushEvent({
        event: ANALYTICS_EVENTS.CANCELLATION_SURVEY_PARTIAL_SAVE,
        subscription_id: subscription?.id,
        step: currentStep,
        demo_mode: isDemoSubscription,
        survey_data: partialData.surveyData,
      });
      return;
    }

    try {
      const response = await fetch(`${apiBaseUrl}/subscription-management/save-partial-response`, {
        method: 'POST',
        headers: await buildHeaders(),
        body: JSON.stringify(partialData),
      });

      if (response.ok) {
        pushEvent({
          event: ANALYTICS_EVENTS.CANCELLATION_SURVEY_PARTIAL_SAVE,
          subscription_id: subscription?.id,
          step: currentStep,
          survey_data: partialData.surveyData,
        });
      }
    } catch {
      // ponytail: partial saves are best effort; blocking cancellation here loses the actual survey.
    }
  };

  const hasActiveDeployments = async (): Promise<boolean> => {
    if (isDemoSubscription || isAuthenticated === false) {
      return false;
    }

    const response = await fetch(`${apiBaseUrl}/deploy/active-deployments`, {
      method: 'GET',
      headers: await buildHeaders(),
    });

    if (!response.ok) {
      return true;
    }

    const data = await response.json().catch(() => ({}));
    return Boolean(
      data?.summary?.hasRunningBots ||
      Number(data?.summary?.totalActive || 0) > 0 ||
      (Array.isArray(data?.deployments) && data.deployments.length > 0)
    );
  };

  const stopUserDeployments = async (reason: string, headers: RequestHeaders): Promise<void> => {
    try {
      await fetch(`${apiBaseUrl}/deploy/stop-user-deployments`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ reason }),
      });
    } catch {
      // ponytail: cancellation still has to proceed if deployment stopping flakes; backend remains source of truth.
    }
  };

  const canProceedFromCurrentStep = (): boolean => {
    switch (currentStep) {
      case 'reason':
        return cancellationReason.length > 0;
      case 'reason_details':
        return cancellationExplanation.length >= MIN_EXPLANATION_LENGTH;
      case 'original_intent':
        return originalIntent.length > 0;
      case 'intent_details':
        return originalIntentExplanation.length >= MIN_INTENT_EXPLANATION_LENGTH;
      case 'pause_preference':
        return pausePreference.length > 0;
      default:
        return true;
    }
  };

  const getContinueButtonErrorMessage = (): string | null => {
    if (canProceedFromCurrentStep()) return null;

    switch (currentStep) {
      case 'reason':
        return 'Please select a cancellation reason to continue.';
      case 'reason_details':
        return `Please write at least ${MIN_EXPLANATION_LENGTH} characters to continue.`;
      case 'original_intent':
        return 'Please select what you hoped to achieve to continue.';
      case 'intent_details':
        return `Please write at least ${MIN_INTENT_EXPLANATION_LENGTH} characters to continue.`;
      case 'pause_preference':
        return 'Please choose whether you want to pause or cancel.';
      default:
        return 'Please complete this step to continue.';
    }
  };

  const goToNextStep = async (): Promise<void> => {
    if (!canProceedFromCurrentStep() || loading) return;

    try {
      setLoading(true);
      setError('');
      await savePartialResponse();

      const currentIndex = steps.indexOf(currentStep);
      let nextStep = steps[currentIndex + 1] || 'complete';

      if (nextStep === 'deployment_impact') {
        const shouldShowDeploymentStep = await hasActiveDeployments();
        setDeploymentStepShown(shouldShowDeploymentStep);
        nextStep = shouldShowDeploymentStep ? 'deployment_impact' : 'reason';
      }

      setCurrentStep(nextStep);
      scrollModalTop();
    } catch (nextError) {
      setError(getErrorMessage(nextError, 'Unable to proceed. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const goToPreviousStep = (): void => {
    if (loading) return;

    if (currentStep === 'reason' && !deploymentStepShown) {
      setCurrentStep('initial');
      return;
    }

    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1]);
      scrollModalTop();
    }
  };

  const processPause = async (pauseDays: number): Promise<void> => {
    if (isDemoSubscription) {
      setAcceptedOfferType(`pause-${pauseDays}`);
      setCurrentStep('complete');
      return;
    }

    const headers = await buildHeaders();
    await stopUserDeployments('subscription_paused', headers);

    const response = await fetch(`${apiBaseUrl}/subscription-management/pause`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        userProductId: subscription?.id,
        pauseDurationDays: pauseDays,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData?.message || errorData?.error || `Failed to pause subscription (${response.status}).`);
    }

    setAcceptedOfferType(`pause-${pauseDays}`);
    setCurrentStep('complete');
    pushEvent({
      event: ANALYTICS_EVENTS.RETENTION_DECISION_MADE,
      subscription_id: subscription?.id,
      decision: 'pause_accepted',
      offer_type: 'pause',
      pause_duration: pauseDays,
    });
  };

  const processCancellation = async (): Promise<void> => {
    if (isDemoSubscription) {
      setCurrentStep('complete');
      return;
    }

    const headers = await buildHeaders();
    await stopUserDeployments('subscription_cancelled', headers);

    const response = await fetch(`${apiBaseUrl}/subscription-management/process-cancellation-survey`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        userProductId: subscription?.id,
        primaryReason: cancellationReason,
        primaryReasonDetails: cancellationExplanation,
        originalIntent,
        originalIntentDetails: originalIntentExplanation,
        chosePause: false,
        pauseDurationDays: null,
        interestedInCall,
        acceptedFreeOffer: false,
        additionalComments: null,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData?.message || errorData?.error || 'Failed to cancel subscription.');
    }

    pushEvent({
      event: ANALYTICS_EVENTS.CANCELLATION_SURVEY_COMPLETED,
      subscription_id: subscription?.id,
      cancellation_reason: cancellationReason,
      pause_preference: pausePreference,
      consultation_interest: interestedInCall,
    });
    pushEvent({
      event: ANALYTICS_EVENTS.RETENTION_DECISION_MADE,
      subscription_id: subscription?.id,
      decision: 'declined_all_offers',
      outcome: 'cancelled',
    });
    setCurrentStep('complete');
  };

  const handleRetentionDecision = async (offerId: string | null, decision: 'accept' | 'decline'): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      if (decision === 'accept' && offerId?.startsWith('pause-')) {
        await processPause(offerId === 'pause-60' ? 60 : 30);
        return;
      }

      await processCancellation();
    } catch (nextError) {
      setError(getErrorMessage(nextError, 'Unable to process your request. Please try again.'));
      setCurrentStep('error');
    } finally {
      setLoading(false);
    }
  };

  const handleFeedbackCall = async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      setInterestedInCall(true);

      if (!isDemoSubscription) {
        const headers = await buildHeaders();
        const pauseDays = pausePreference === '60_days' ? 60 : (pausePreference === '30_days' ? 30 : null);
        const response = await fetch(`${apiBaseUrl}/subscription-management/apply-feedback-call-benefits`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            userProductId: subscription?.id,
            pauseDurationDays: pauseDays,
            originalChoice: pauseDays ? 'pause' : 'cancel',
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          if (!errorData?.alreadyApplied && !String(errorData?.message || '').includes('already received')) {
            throw new Error(errorData?.message || 'Failed to apply feedback call benefits.');
          }
        }
      }

      window.open('https://calendly.com/lumiwealth/botspot-feedback-call', '_blank');
      setAcceptedOfferType('feedback-call');
      setCurrentStep('complete');
      pushEvent({
        event: ANALYTICS_EVENTS.RETENTION_DECISION_MADE,
        subscription_id: subscription?.id,
        decision: 'feedback_call_accepted',
        offer_type: 'feedback_call_with_benefits',
      });
    } catch (nextError) {
      setError(getErrorMessage(nextError, 'Failed to apply benefits. Please try again or contact support.'));
    } finally {
      setLoading(false);
    }
  };

  const handleManualClose = (outcome: string, retentionAccepted = false): void => {
    onComplete?.({
      success: true,
      outcome,
      subscriptionId: subscription?.id,
      retentionOfferAccepted: retentionAccepted,
    });
  };

  const handleModalClose = async (): Promise<void> => {
    if (loading) return;

    if (!['initial', 'complete', 'error'].includes(currentStep)) {
      await savePartialResponse();
      pushEvent({
        event: ANALYTICS_EVENTS.CANCELLATION_SURVEY_ABANDONED,
        subscription_id: subscription?.id,
        step_abandoned: currentStep,
        survey_data: surveyData(),
      });
    }

    onHide?.();
  };

  const getModalTitle = (): string => {
    const titles: Record<CancellationStep, string> = {
      initial: 'Cancel Subscription',
      deployment_impact: 'Impact on Your Trading Bots',
      reason: 'Step 1 of 6: Cancellation Reason',
      reason_details: 'Step 2 of 6: Tell Us More',
      original_intent: 'Step 3 of 6: Your Goals',
      intent_details: 'Step 4 of 6: Expected Outcomes',
      pause_preference: 'Step 5 of 6: Pause Option',
      call_interest: 'Step 6 of 6: Feedback Call',
      complete: 'Complete',
      error: 'Error',
    };

    return titles[currentStep];
  };

  const showProgress = surveySteps.includes(currentStep);
  const progressPercent = showProgress
    ? ((surveySteps.indexOf(currentStep) + 1) / surveySteps.length) * 100
    : 0;

  const renderRadio = (
    id: string,
    name: string,
    label: string,
    checked: boolean,
    onChange: (value: string) => void,
  ): React.ReactElement => (
    <Form.Check
      key={id}
      type="radio"
      id={id}
      name={name}
      label={label}
      value={label}
      checked={checked}
      onChange={(event) => onChange(event.currentTarget.value)}
    />
  );

  const renderStepContent = (): React.ReactNode => {
    switch (currentStep) {
      case 'initial':
        return (
          <div className="cancellation-initial text-center">
            <span className="cancellation-status-icon" aria-hidden="true">
              <FontAwesomeIcon icon={faExclamationTriangle} />
            </span>
            <h4 className="cancellation-heading">Before you cancel</h4>
            <p className="cancellation-subtitle">
              We will ask a few quick questions so we can process the cancellation correctly and offer pause options if they fit.
            </p>

            <div className="subscription-summary">
              <h6 className="summary-label">You are about to cancel</h6>
              <div className="subscription-details-row">
                <span className="plan-name">{planName}</span>
                {formattedPrice ? <span className="plan-price">{formattedPrice}/{billingInterval}</span> : null}
              </div>
              <div className="loss-indicator">
                <small>Your subscription benefits and any running trading workflows may stop after cancellation.</small>
              </div>
            </div>
          </div>
        );

      case 'deployment_impact':
        return (
          <div className="deployment-impact-step">
            <h4>
              <FontAwesomeIcon icon={faRobot} className="me-2" />
              Review active deployments
            </h4>
            <DeploymentImpactWarning show />
          </div>
        );

      case 'reason':
        return (
          <div className="survey-question">
            <h4>What is the main reason you are canceling?</h4>
            <p>Select the option that best describes your situation.</p>
            <Form>
              {randomizedReasons.map((reason) => renderRadio(
                `reason-${reason}`,
                'cancellationReason',
                reason,
                cancellationReason === reason,
                setCancellationReason,
              ))}
            </Form>
          </div>
        );

      case 'reason_details':
        return (
          <div className="survey-question">
            <h4>Help us understand your situation</h4>
            <p>You selected: <strong>{cancellationReason}</strong></p>
            <Form.Group>
              <Form.Control
                as="textarea"
                rows={4}
                value={cancellationExplanation}
                onChange={(event) => setCancellationExplanation(event.currentTarget.value)}
                placeholder="Please explain your situation in more detail..."
                maxLength={500}
              />
              <Form.Text>
                {cancellationExplanation.length} / {MIN_EXPLANATION_LENGTH} characters minimum
              </Form.Text>
            </Form.Group>
          </div>
        );

      case 'original_intent':
        return (
          <div className="survey-question">
            <h4>What were you hoping to achieve with BotSpot?</h4>
            <p>Understanding your goal helps us fix the right problems.</p>
            <Form>
              {randomizedIntents.map((intent) => renderRadio(
                `intent-${intent}`,
                'originalIntent',
                intent,
                originalIntent === intent,
                setOriginalIntent,
              ))}
            </Form>
          </div>
        );

      case 'intent_details':
        return (
          <div className="survey-question">
            <h4>Tell us about your trading goals</h4>
            <p>You wanted: <strong>{originalIntent}</strong></p>
            <Form.Group>
              <Form.Control
                as="textarea"
                rows={3}
                value={originalIntentExplanation}
                onChange={(event) => setOriginalIntentExplanation(event.currentTarget.value)}
                placeholder="Describe what success would have looked like for you..."
                maxLength={300}
              />
              <Form.Text>
                {originalIntentExplanation.length} / {MIN_INTENT_EXPLANATION_LENGTH} characters minimum
              </Form.Text>
            </Form.Group>
          </div>
        );

      case 'pause_preference':
        return (
          <div className="survey-question">
            <h4>Would you consider pausing instead?</h4>
            <p>You can pause and keep your settings ready for later, or continue with cancellation.</p>
            <Form>
              {renderRadio('pause-30', 'pausePreference', 'Yes, pause for 30 days', pausePreference === '30_days', () => setPausePreference('30_days'))}
              {renderRadio('pause-60', 'pausePreference', 'Yes, pause for 60 days', pausePreference === '60_days', () => setPausePreference('60_days'))}
              {renderRadio('no-pause', 'pausePreference', 'No, I want to cancel permanently', pausePreference === 'no', () => setPausePreference('no'))}
            </Form>
          </div>
        );

      case 'call_interest': {
        const monthlyPrice = Number(subscription?.amount || 0) / 100;
        const threeMonthsValue = monthlyPrice * 3;
        const expertCallValue = 200;
        const expertSupportValue = 250;
        const totalValue = threeMonthsValue + expertCallValue + expertSupportValue;

        return (
          <div className="feedback-call-header">
            <h4 className="modal-heading">
              Get {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(totalValue)}+ in benefits for a feedback call
            </h4>

            <div className="benefits-highlight">
              <div className="benefits-title">What you get</div>
              <div className="benefits-grid">
                <div className="benefit-item">
                  <div className="benefit-icon"><FontAwesomeIcon icon={faGift} /></div>
                  <div className="benefit-content">
                    <div className="benefit-title">3 months free</div>
                    <div className="benefit-subtitle">{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(threeMonthsValue)} value</div>
                  </div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon"><FontAwesomeIcon icon={faPhone} /></div>
                  <div className="benefit-content">
                    <div className="benefit-title">Expert strategy call</div>
                    <div className="benefit-subtitle">$200 value</div>
                  </div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon"><FontAwesomeIcon icon={faCheckCircle} /></div>
                  <div className="benefit-content">
                    <div className="benefit-title">Expert help and support</div>
                    <div className="benefit-subtitle">$250 value</div>
                  </div>
                </div>
              </div>
              <div className="benefits-note">
                <span className="benefits-note__title">Benefits apply when you book.</span>
                <span className="benefits-note__body">The call helps us understand what did not work and what you were trying to build.</span>
              </div>
            </div>

            <div className="action-buttons">
              <Button variant="success" onClick={handleFeedbackCall} className="primary-action-btn" disabled={loading}>
                {loading ? <Spinner size="sm" className="me-2" /> : <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />}
                Book My Free Call
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => {
                  if (pausePreference === '30_days' || pausePreference === '60_days') {
                    void handleRetentionDecision(pausePreference === '60_days' ? 'pause-60' : 'pause-30', 'accept');
                  } else {
                    void handleRetentionDecision(null, 'decline');
                  }
                }}
                className="secondary-action-btn"
                disabled={loading}
              >
                {pausePreference === '30_days' || pausePreference === '60_days'
                  ? `No Thanks, Just Pause ${pausePreference === '60_days' ? '60' : '30'} Days`
                  : 'No Thanks, Just Cancel'}
              </Button>
            </div>
          </div>
        );
      }

      case 'complete':
        return (
          <div className="cancellation-complete text-center">
            {acceptedOfferType === 'feedback-call' ? (
              <>
                <FontAwesomeIcon icon={faGift} size="3x" className="text-success mb-3" />
                <h4>Your benefits are ready</h4>
                <Alert variant="success" className="mb-4">
                  Your account credit has been applied. Book and attend the feedback call to keep these benefits.
                </Alert>
                <Button variant="primary" size="lg" onClick={() => handleManualClose('feedback_call_booked', true)}>
                  Close
                </Button>
              </>
            ) : acceptedOfferType === 'pause-30' || acceptedOfferType === 'pause-60' ? (
              <>
                <FontAwesomeIcon icon={faPause} size="3x" className="text-success mb-3" />
                <h4>Subscription paused</h4>
                <Alert variant="success" className="mb-4">
                  Your subscription has been paused for {acceptedOfferType === 'pause-60' ? '60' : '30'} days.
                </Alert>
                <Button variant="primary" size="lg" onClick={() => handleManualClose('subscription_paused', true)}>
                  Close
                </Button>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faExclamationTriangle} size="3x" className="text-warning mb-3" />
                <h4>Subscription cancelled</h4>
                <Alert variant="info" className="mb-4">
                  Thank you for the feedback. Your account and settings will remain available if you return.
                </Alert>
                <Button variant="primary" size="lg" onClick={() => handleManualClose('subscription_cancelled', false)}>
                  Close
                </Button>
              </>
            )}
          </div>
        );

      case 'error':
        return (
          <div className="cancellation-error text-center">
            <FontAwesomeIcon icon={faExclamationTriangle} size="3x" className="text-danger mb-3" />
            <h4>Error processing request</h4>
            <Alert variant="danger" className="text-start">{error}</Alert>
            <div className="error-actions">
              <Button variant="primary" onClick={() => { setError(''); setCurrentStep('initial'); }}>
                Try Again
              </Button>
              <Button variant="outline-secondary" onClick={() => handleManualClose('error', false)}>
                Close
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const renderFooter = (): React.ReactNode => {
    if (currentStep === 'initial') {
      return (
        <div className="d-flex flex-column flex-sm-row justify-content-end gap-2">
          <Button variant="outline-secondary" onClick={handleModalClose} disabled={loading}>
            Keep Subscription
          </Button>
          <Button variant="danger" onClick={goToNextStep} disabled={loading}>
            {loading ? <Spinner size="sm" className="me-2" /> : <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />}
            Continue to Cancel
          </Button>
        </div>
      );
    }

    if (currentStep === 'deployment_impact' || ['reason', 'reason_details', 'original_intent', 'intent_details', 'pause_preference'].includes(currentStep)) {
      const blocker = getContinueButtonErrorMessage();
      return (
        <div className="d-flex flex-column flex-sm-row justify-content-between gap-2">
          <Button variant="outline-secondary" onClick={goToPreviousStep} disabled={loading}>
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back
          </Button>
          <Button
            variant="primary"
            onClick={goToNextStep}
            disabled={Boolean(blocker) || loading}
            title={blocker || undefined}
          >
            {loading ? <Spinner size="sm" className="me-2" /> : null}
            Continue
            {!loading ? <FontAwesomeIcon icon={faArrowRight} className="ms-2" /> : null}
          </Button>
        </div>
      );
    }

    return null;
  };

  if (!show) {
    return null;
  }

  const blocker = getContinueButtonErrorMessage();

  return (
    <ContentModal
      isOpen={show}
      onClose={handleModalClose}
      title={getModalTitle()}
      size="lg"
      className="cancellation-modal"
      closeOnOverlayClick={false}
      footer={renderFooter()}
      data-testid="cancellation-modal"
    >
      {showProgress ? (
        <div className="cancellation-progress" aria-label={`Step ${surveySteps.indexOf(currentStep) + 1} of ${surveySteps.length}`}>
          <div className="cancellation-progress-bar" aria-hidden="true">
            <div className="progress-bar" style={{ width: `${progressPercent}%` }} />
          </div>
          <div className="text-muted small mt-2">Step {surveySteps.indexOf(currentStep) + 1} of {surveySteps.length}</div>
        </div>
      ) : null}

      <div className="d-flex flex-column gap-3">
        {error && currentStep !== 'error' ? <Alert variant="danger">{error}</Alert> : null}
        {blocker && ['reason', 'reason_details', 'original_intent', 'intent_details', 'pause_preference'].includes(currentStep) ? (
          <Alert variant="warning">{blocker}</Alert>
        ) : null}
        {renderStepContent()}
      </div>
    </ContentModal>
  );
};

export default SubscriptionCancellationModal;
